import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { updateRiskAnalysisStep } from "@/utils/session";
import { RISK_WIZARD_STEPS } from "@/constants/steps";
import type {
  CyFunLevel,
  OrganizationSize,
  RiskAnalysisItem,
  Sector,
  SectorAnalysis,
} from "@/types/risk-analysis";
import type { EntityData } from "@/types/entity";
import type { DecizieInregistrare } from "@/types/decizieInregistrare";
import type { WizardSteps } from "@/types/risk-wizard";
import { useGetRiskAnalysisBySession } from "@/hooks/api/query/useGetRiskAnalysisBySession";
import { useGetSectors } from "@/hooks/api/query/useGetSectors";
import { useGetEntityData } from "@/hooks/api/query/useGetEntityData";
import { useGetDecizieInregistrare } from "@/hooks/api/query/useGetDecizieInregistrare";
import { buildWizardSteps } from "@/utils/risk-wizard/wizardStepBuilder";
import {
  mapEntitySizeToOrganizationSize,
  transformRiskAnalysisItemsToSectorAnalyses,
} from "@/utils/risk-analysis-transform";

type UseRiskWizardOrchestratorReturn = {
  sessionId: string;
  companyName: string;
  cui: string;
  organizationSize: OrganizationSize | null;
  entityData: EntityData | null;
  decizie: DecizieInregistrare | null;
  isReturningUser: boolean;
  selectedSectorIds: string[];
  sectorAnalysesMap: Record<string, SectorAnalysis>;
  riskAnalysisIdMap: Record<string, string>;
  riskAnalyses: RiskAnalysisItem[];
  globalScore: number;
  globalCyFunLevel: CyFunLevel;
  totalModifications: number;
  sectors: Sector[];
  wizardSteps: WizardSteps;
  isLoading: boolean;
  error: Error | null;

  currentMainStep: number;
  currentSectorIndex: number;
  skippedSectorIds: Set<string>;
  isSaving: boolean;

  setCurrentMainStep: (step: number) => void;
  setCurrentSectorIndex: (index: number) => void;
  setIsSaving: (saving: boolean) => void;
  addSkippedSector: (sectorId: string) => void;
};

export function useRiskWizardOrchestrator(
  sessionId: string
): UseRiskWizardOrchestratorReturn {
  const {
    data: sessionData,
    isLoading: isSessionLoading,
    error: sessionError,
  } = useGetRiskAnalysisBySession(sessionId);

  const {
    data: sectorsData,
    isLoading: isSectorsLoading,
    error: sectorsError,
  } = useGetSectors();

  const { data: entityDataResponse, isLoading: isEntityDataLoading } =
    useGetEntityData(sessionId, { enabled: !!sessionId });

  const { data: decizieData, isLoading: isDecizieLoading } =
    useGetDecizieInregistrare(sessionId || null);

  const [currentMainStep, setCurrentMainStep] = useState(1);
  const [currentSectorIndex, setCurrentSectorIndex] = useState(0);
  const [skippedSectorIds, setSkippedSectorIds] = useState<Set<string>>(
    new Set()
  );
  const [isSaving, setIsSaving] = useState(false);

  const sectors = sectorsData ?? [];
  const riskAnalyses = sessionData?.risk_analyses ?? [];

  const organizationSize = useMemo(
    () => mapEntitySizeToOrganizationSize(sessionData?.entity_size ?? null),
    [sessionData?.entity_size]
  );

  const isReturningUser = riskAnalyses.length > 0;

  const selectedSectorIds = useMemo(
    () => riskAnalyses.map((ra) => ra.sector_id),
    [riskAnalyses]
  );

  const sectorAnalysesMap = useMemo(() => {
    if (
      !organizationSize ||
      riskAnalyses.length === 0 ||
      sectors.length === 0
    ) {
      return {};
    }
    return transformRiskAnalysisItemsToSectorAnalyses(
      riskAnalyses,
      sectors,
      organizationSize
    );
  }, [riskAnalyses, sectors, organizationSize]);

  const riskAnalysisIdMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const ra of riskAnalyses) {
      map[ra.sector_id] = ra.id;
    }
    return map;
  }, [riskAnalyses]);

  const globalScore = sessionData?.global_score ?? 0;
  const globalCyFunLevel: CyFunLevel = sessionData?.global_cyfun_level ?? null;

  const totalModifications = useMemo(
    () => riskAnalyses.filter((ra) => ra.has_populated_matrix).length,
    [riskAnalyses]
  );

  const wizardSteps = useMemo(() => {
    const steps = buildWizardSteps(selectedSectorIds, sectors);

    steps.currentMainStep = currentMainStep;
    steps.currentSectorIndex = currentSectorIndex;

    steps.mainSteps = steps.mainSteps.map((step) => ({
      ...step,
      status:
        step.id < currentMainStep
          ? "completed"
          : step.id === currentMainStep
            ? "current"
            : "upcoming",
    }));

    steps.sectorSteps = steps.sectorSteps.map((step, idx) => {
      const ra = riskAnalyses.find((r) => r.sector_id === step.sectorId);
      const isComplete = ra?.has_populated_matrix ?? false;
      const isSkipped = skippedSectorIds.has(step.sectorId);

      return {
        ...step,
        status: isComplete
          ? "completed"
          : idx === currentSectorIndex
            ? "current"
            : isSkipped
              ? "skipped"
              : "pending",
      };
    });

    return steps;
  }, [
    selectedSectorIds,
    sectors,
    currentMainStep,
    currentSectorIndex,
    riskAnalyses,
    skippedSectorIds,
  ]);

  const addSkippedSector = useCallback((sectorId: string) => {
    setSkippedSectorIds((prev) => new Set([...prev, sectorId]));
  }, []);

  useEffect(() => {
    setSkippedSectorIds((prev) => {
      if (prev.size === 0) return prev;
      const populatedIds = new Set(
        riskAnalyses
          .filter((ra) => ra.has_populated_matrix)
          .map((ra) => ra.sector_id)
      );
      const next = new Set([...prev].filter((id) => !populatedIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [riskAnalyses]);

  const hasSeededStepRef = useRef(false);

  useEffect(() => {
    if (hasSeededStepRef.current) return;
    if (isSessionLoading || !sessionData) return;

    hasSeededStepRef.current = true;
    const stored = sessionData.risk_analysis_current_step ?? 1;
    if (stored < 1 || stored > RISK_WIZARD_STEPS.length) {
      console.error(
        `Invalid risk_analysis_current_step=${stored}; resetting to 1`
      );
      setCurrentMainStep(1);
      return;
    }
    if (stored !== currentMainStep) {
      setCurrentMainStep(stored);
    }
  }, [isSessionLoading, sessionData, currentMainStep]);

  useEffect(() => {
    if (!hasSeededStepRef.current) return;
    if (!sessionId) return;
    updateRiskAnalysisStep(sessionId, currentMainStep).catch((error) => {
      console.error("Failed to persist risk analysis step:", error);
    });
  }, [currentMainStep, sessionId]);

  const isLoading =
    isSessionLoading ||
    isSectorsLoading ||
    isEntityDataLoading ||
    isDecizieLoading;
  const error = sessionError || sectorsError || null;

  return {
    sessionId,
    companyName: sessionData?.nume_entitate ?? "",
    cui: sessionData?.cui ?? "",
    organizationSize,
    entityData: entityDataResponse?.entity_data ?? null,
    decizie: decizieData ?? null,
    isReturningUser,
    selectedSectorIds,
    sectorAnalysesMap,
    riskAnalysisIdMap,
    riskAnalyses,
    globalScore,
    globalCyFunLevel,
    totalModifications,
    sectors,
    wizardSteps,
    isLoading,
    error,

    currentMainStep,
    currentSectorIndex,
    skippedSectorIds,
    isSaving,

    setCurrentMainStep,
    setCurrentSectorIndex,
    setIsSaving,
    addSkippedSector,
  };
}
